import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import {
  BsArrowLeft,
  BsCloudUpload,
  BsEye,
  BsEyeSlash,
  BsPersonFill,
} from "react-icons/bs";

import Sidebar from "../../components/sidebar/Sidebar";

import { servicePostCreateUser } from "../../api/serviceApi";

import {
  serviceDeletePhoto,
  serviceUploadPhoto,
} from "../../api/servicePhotoApi";

import "./addemployee.css";

const MAX_IMAGE_SIZE = 5 * 1024 * 1024;

const INITIAL_FORM = {
  username: "",
  email: "",
  password: "",
  name: "",
  role: "",

  address: "",
  phone: "",
  employmentType: "",
  dob: "",
};

const ROLE_OPTIONS = [
  "ADMIN",
  "MANAGER",
  "TECHNICIAN",
  "FITTER",
  "DISTRIBUTOR",
];

// ============================================================
// HELPERS
// ============================================================

const isImageFile = (file) => {
  if (file?.type?.startsWith("image/")) {
    return true;
  }

  return /\.(jpg|jpeg|png|webp|heic|heif|avif)$/i.test(file?.name || "");
};

const makeProfileFilename = (email) => {
  const safeEmail = String(email || "user")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9_-]+/g, "_");

  return [
    safeEmail,
    "profile",
    Date.now(),
    Math.random().toString(36).slice(2, 8),
  ].join("-");
};

// ============================================================
// COMPONENT
// ============================================================

const NewUser = () => {
  const navigate = useNavigate();

  const [form, setForm] = useState(INITIAL_FORM);

  const [image, setImage] = useState(null);

  const [previewUrl, setPreviewUrl] = useState("");

  const [showPassword, setShowPassword] = useState(false);

  const [submitting, setSubmitting] = useState(false);

  const [uploadingPhoto, setUploadingPhoto] = useState(false);

  // ==========================================================
  // LOCAL IMAGE PREVIEW
  // ==========================================================

  useEffect(() => {
    if (!image) {
      setPreviewUrl("");
      return;
    }

    const objectUrl = URL.createObjectURL(image);

    setPreviewUrl(objectUrl);

    return () => {
      URL.revokeObjectURL(objectUrl);
    };
  }, [image]);

  // ==========================================================
  // IMAGE SELECTION
  // ==========================================================

  const handleImageChoose = (event) => {
    const file = event.target.files?.[0];

    /*
     * Allows choosing the same file again.
     */
    event.target.value = "";

    if (!file) {
      return;
    }

    if (!isImageFile(file)) {
      toast.error("Please select a valid image file.");
      return;
    }

    if (file.size > MAX_IMAGE_SIZE) {
      toast.error("Profile image must be 5MB or smaller.");
      return;
    }

    setImage(file);
  };

  const removeImage = () => {
    if (submitting) {
      return;
    }

    setImage(null);
  };

  // ==========================================================
  // FORM
  // ==========================================================

  const handleInputChange = (event) => {
    const { name, value } = event.target;

    setForm((previous) => ({
      ...previous,
      [name]: value,
    }));
  };

  // ==========================================================
  // UPLOAD PROFILE IMAGE
  // ==========================================================

  const uploadProfileImage = async () => {
    if (!image) {
      return "";
    }

    try {
      setUploadingPhoto(true);

      const result = await serviceUploadPhoto({
        file: image,
        folder: "tims/profilePictures",
        filename: makeProfileFilename(form.email),
      });

      if (!result?.url) {
        throw new Error("Profile image upload failed.");
      }

      return result.url;
    } finally {
      setUploadingPhoto(false);
    }
  };

  // ==========================================================
  // SUBMIT
  // ==========================================================

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (submitting) {
      return;
    }

    const email = form.email.trim();
    const username = form.username.trim();
    const name = form.name.trim();

    if (!email) {
      toast.error("Email is required.");
      return;
    }

    if (!username) {
      toast.error("Username is required.");
      return;
    }

    if (!form.password) {
      toast.error("Password is required.");
      return;
    }

    if (form.password.length < 6) {
      toast.error("Password must be at least 6 characters.");
      return;
    }

    if (!form.role) {
      toast.error("Role is required.");
      return;
    }

    /*
     * If the photo uploads but user creation fails,
     * this URL is used to clean up the orphaned file.
     */
    let uploadedImageUrl = "";

    try {
      setSubmitting(true);

      // ------------------------------------------------------
      // 1. OPTIONAL PROFILE PHOTO
      // ------------------------------------------------------

      uploadedImageUrl = await uploadProfileImage();

      // ------------------------------------------------------
      // 2. CREATE USER THROUGH API
      // ------------------------------------------------------

      const payload = {
        username,
        email,
        password: form.password,

        name,

        role: form.role,

        address: form.address.trim(),

        phone: form.phone.trim(),

        employmentType: form.employmentType.trim(),

        /*
         * Empty string is preserved here.
         * Change to null if your API schema prefers null.
         */
        dob: form.dob || "",

        imageUrl: uploadedImageUrl,
      };

      await servicePostCreateUser(payload);

      toast.success("Employee added successfully.");

      navigate("/employees");
    } catch (error) {
      console.error("Error adding employee:", error);

      /*
       * Photo succeeded but user creation failed.
       * Remove the orphaned profile image.
       */
      if (uploadedImageUrl) {
        try {
          await serviceDeletePhoto(uploadedImageUrl);
        } catch (cleanupError) {
          console.error(
            "Failed to clean up uploaded profile image:",
            cleanupError,
          );
        }
      }

      toast.error(
        error?.message || "Failed to add employee. Please try again.",
      );
    } finally {
      setSubmitting(false);
      setUploadingPhoto(false);
    }
  };

  // ==========================================================
  // RENDER
  // ==========================================================

  return (
    <div className="new-employee-page">
      <Sidebar />

      <main className="new-employee-content">
        <div className="new-employee-container">
          {/* ================================================
              HEADER
          ================================================= */}

          <header className="new-employee-header">
            <div>
              <button
                type="button"
                className="new-employee-back"
                onClick={() => navigate(-1)}
                disabled={submitting}
              >
                <BsArrowLeft />
                Back
              </button>

              <h1>Add Employee</h1>

              <p>Create a new employee account and profile.</p>
            </div>
          </header>

          {/* ================================================
              FORM CARD
          ================================================= */}

          <form className="new-employee-card" onSubmit={handleSubmit}>
            {/* ==============================================
                PROFILE PHOTO
            =============================================== */}

            <aside className="new-employee-photo">
              <div className="employee-photo-preview">
                {previewUrl ? (
                  <img src={previewUrl} alt="Employee preview" />
                ) : (
                  <BsPersonFill />
                )}
              </div>

              <div className="employee-photo-info">
                <strong>Profile Photo</strong>

                <span>Upload an optional employee profile image.</span>
              </div>

              <label
                htmlFor="employee-photo"
                className={`employee-upload-btn ${
                  submitting ? "disabled" : ""
                }`}
              >
                <BsCloudUpload />

                {image ? "Change Photo" : "Upload Photo"}
              </label>

              <input
                id="employee-photo"
                type="file"
                accept="image/*"
                className="employee-file-input"
                onChange={handleImageChoose}
                disabled={submitting}
              />

              {image && (
                <div className="employee-selected-file">
                  <span title={image.name}>{image.name}</span>

                  <button
                    type="button"
                    onClick={removeImage}
                    disabled={submitting}
                  >
                    Remove
                  </button>
                </div>
              )}

              {submitting && image && (
                <div className="employee-upload-progress">
                  <div className="employee-progress-header">
                    <span>
                      {uploadingPhoto ? "Uploading photo" : "Photo ready"}
                    </span>

                    <strong>{uploadingPhoto ? "..." : "✓"}</strong>
                  </div>
                </div>
              )}
            </aside>

            {/* ==============================================
                FORM DETAILS
            =============================================== */}

            <div className="new-employee-form">
              {/* ACCOUNT DETAILS */}

              <div className="new-employee-section-header">
                <h2>Account Details</h2>

                <p>Login information for the employee.</p>
              </div>

              <div className="employee-form-grid">
                {/* EMAIL */}

                <div className="employee-form-field">
                  <label htmlFor="email">
                    Email <span>*</span>
                  </label>

                  <input
                    id="email"
                    type="email"
                    name="email"
                    value={form.email}
                    placeholder="employee@example.com"
                    onChange={handleInputChange}
                    autoComplete="email"
                    disabled={submitting}
                    required
                  />
                </div>

                {/* USERNAME */}

                <div className="employee-form-field">
                  <label htmlFor="username">
                    Username <span>*</span>
                  </label>

                  <input
                    id="username"
                    type="text"
                    name="username"
                    value={form.username}
                    placeholder="e.g. jsmith"
                    onChange={handleInputChange}
                    autoComplete="username"
                    disabled={submitting}
                    required
                  />
                </div>

                {/* PASSWORD */}
                <div className="employee-form-field">
                  <label htmlFor="password">
                    Password <span>*</span>
                  </label>

                  <div className="employee-password-control">
                    <input
                      id="password"
                      type={showPassword ? "text" : "password"}
                      name="password"
                      value={form.password}
                      placeholder="Minimum 6 characters"
                      onChange={handleInputChange}
                      autoComplete="new-password"
                      minLength={6}
                      disabled={submitting}
                      required
                    />

                    <button
                      type="button"
                      className="employee-password-toggle"
                      disabled={submitting}
                      onClick={() => setShowPassword((previous) => !previous)}
                      aria-label={
                        showPassword ? "Hide password" : "Show password"
                      }
                      title={showPassword ? "Hide password" : "Show password"}
                    >
                      {showPassword ? <BsEyeSlash /> : <BsEye />}
                    </button>
                  </div>
                </div>

                {/* ROLE */}
                <div className="employee-form-field">
                  <label htmlFor="role">
                    Role <span>*</span>
                  </label>

                  <select
                    id="role"
                    name="role"
                    value={form.role}
                    onChange={handleInputChange}
                    disabled={submitting}
                    required
                  >
                    <option value="" disabled>
                      Select role...
                    </option>

                    {ROLE_OPTIONS.map((role) => (
                      <option key={role} value={role}>
                        {role}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="new-employee-divider" />

              {/* ============================================
                  PERSONAL DETAILS
              ============================================= */}

              <div className="new-employee-section-header">
                <h2>Personal Details</h2>

                <p>Employee contact and personal information.</p>
              </div>

              <div className="employee-form-grid">
                {/* NAME */}

                <div className="employee-form-field">
                  <label htmlFor="name">Name</label>

                  <input
                    id="name"
                    type="text"
                    name="name"
                    value={form.name}
                    placeholder="Employee name"
                    onChange={handleInputChange}
                    disabled={submitting}
                  />
                </div>

                {/* PHONE */}

                <div className="employee-form-field">
                  <label htmlFor="phone">Phone</label>

                  <input
                    id="phone"
                    type="tel"
                    name="phone"
                    value={form.phone}
                    placeholder="Phone number"
                    onChange={handleInputChange}
                    disabled={submitting}
                  />
                </div>

                {/* ADDRESS */}

                <div className="employee-form-field employee-full-width">
                  <label htmlFor="address">Address</label>

                  <input
                    id="address"
                    type="text"
                    name="address"
                    value={form.address}
                    placeholder="Employee address"
                    onChange={handleInputChange}
                    disabled={submitting}
                  />
                </div>

                {/* DOB */}

                <div className="employee-form-field">
                  <label htmlFor="dob">Date of Birth</label>

                  <input
                    id="dob"
                    type="date"
                    name="dob"
                    value={form.dob}
                    onChange={handleInputChange}
                    disabled={submitting}
                  />
                </div>

                {/* EMPLOYMENT TYPE */}

                <div className="employee-form-field">
                  <label htmlFor="employmentType">Employment Type</label>

                  <input
                    id="employmentType"
                    type="text"
                    name="employmentType"
                    value={form.employmentType}
                    placeholder="Full Time, Part Time..."
                    onChange={handleInputChange}
                    disabled={submitting}
                  />
                </div>
              </div>

              {/* ============================================
                  ACTIONS
              ============================================= */}

              <div className="new-employee-actions">
                <button
                  type="button"
                  className="new-employee-btn ghost"
                  onClick={() => navigate(-1)}
                  disabled={submitting}
                >
                  Cancel
                </button>

                <button
                  type="submit"
                  className="new-employee-btn primary"
                  disabled={submitting}
                >
                  {submitting
                    ? uploadingPhoto
                      ? "Uploading Photo..."
                      : "Creating Employee..."
                    : "Create Employee"}
                </button>
              </div>
            </div>
          </form>
        </div>
      </main>
    </div>
  );
};

export default NewUser;
